import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { API } from "@/lib/constants";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import AEOGEOSection from "@/components/AEOGEOSection";
import PortfolioSection from "@/components/PortfolioSection";
import CaseStudiesSection from "@/components/CaseStudiesSection";
import GoogleReviewsSection from "@/components/GoogleReviewsSection";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import MostHelpfulArticle from "@/components/MostHelpfulArticle";
import ClientSitesStrip from "@/components/ClientSitesStrip";
import TexasCoverageWidget from "@/components/TexasCoverageWidget";
import ReviewProofStrip from "@/components/ReviewProofStrip";

// SEO Constants - Optimized for Page 1 Google Rankings
const SEO = {
  title: "Digital Media Ninja | Dallas SEO Company | DFW Digital Marketing Agency",
  description: "Dallas's leading SEO company & digital marketing agency. Expert Local SEO, Google Ads management, web design, PPC, social media marketing & content marketing in Dallas-Fort Worth. Get found in Google, ChatGPT & AI search. Free audit!",
  keywords: "Dallas SEO company, DFW digital marketing agency, Local SEO Dallas, Google Business Profile optimization Dallas, WordPress web design Dallas, PPC management Dallas, Dallas social media marketing, Content marketing Dallas, SEO services Dallas Texas, Digital marketing consultant Dallas, Google Ads management Dallas, Local business marketing Dallas, Website optimization Dallas, Online marketing Dallas, Search engine optimization DFW, Marketing agency Dallas Texas",
  image: "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
  url: "https://digitalmedianinja.com"
};

// Homepage Schema
const getHomePageSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://digitalmedianinja.com/#organization",
      "name": "Digital Media Ninja",
      "url": "https://digitalmedianinja.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG"
      },
      "description": "Dallas digital marketing agency specializing in SEO, AEO (Answer Engine Optimization), and GEO (Generative Engine Optimization). We help Dallas businesses dominate traditional and AI-powered search.",
      "sameAs": [
        "https://www.facebook.com/digitalmedianinja",
        "https://twitter.com/dmninja",
        "https://www.linkedin.com/company/digitalmedianinja"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "3333 Harry Hines Blvd",
        "addressLocality": "Dallas",
        "addressRegion": "TX",
        "postalCode": "75201",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-432-934-9470",
        "contactType": "customer service",
        "availableLanguage": ["English", "Spanish"]
      },
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 32.7767,
          "longitude": -96.7970
        },
        "geoRadius": "50 mi"
      }
    },
    {
      "@type": "LocalBusiness",
      "@id": "https://digitalmedianinja.com/#localbusiness",
      "name": "Digital Media Ninja",
      "image": "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
      "url": "https://digitalmedianinja.com",
      "telephone": "+1-432-934-9470",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "3333 Harry Hines Blvd",
        "addressLocality": "Dallas",
        "addressRegion": "TX",
        "postalCode": "75201",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 32.7767,
        "longitude": -96.797
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "17",
        "bestRating": "5"
      }
    },
    {
      "@type": "WebSite",
      "@id": "https://digitalmedianinja.com/#website",
      "url": "https://digitalmedianinja.com",
      "name": "Digital Media Ninja - Dallas SEO & AEO Agency",
      "publisher": { "@id": "https://digitalmedianinja.com/#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://digitalmedianinja.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  ]
});

const HomePage = ({ onOpenPopup }) => {
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    // Set page title directly to ensure it works
    document.title = SEO.title;
    
    const fetchData = async () => {
      try {
        const [servicesRes, faqsRes, caseStudiesRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/faqs`),
          axios.get(`${API}/case-studies`)
        ]);
        setServices(servicesRes.data);
        setFaqs(faqsRes.data);
        setCaseStudies(caseStudiesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <link rel="canonical" href={SEO.url} />
        
        {/* Open Graph */}
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Digital Media Ninja" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
        <meta name="twitter:image" content={SEO.image} />
        
        {/* Local SEO */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        <meta name="geo.position" content="32.7767;-96.7970" />
        <meta name="ICBM" content="32.7767, -96.7970" />
      </Helmet>

      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getHomePageSchema()) }} />

      <HeroSection onOpenPopup={onOpenPopup} />
      <ReviewProofStrip />
      <ClientSitesStrip />
      <ServicesSection services={services} />
      <PortfolioSection />
      <AEOGEOSection onOpenPopup={onOpenPopup} />
      <CaseStudiesSection caseStudies={caseStudies} />
      <TexasCoverageWidget variant="full" />
      <GoogleReviewsSection />
      <MostHelpfulArticle />
      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default HomePage;
